import { Mark, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state'
import { Fragment, Slice } from 'prosemirror-model'
import { useSpaceVariableStore } from '@/modules/space/stores/SpaceVariableStore'
import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import { useAppStore } from '@/modules/global/stores/AppStore'
import { usePermissionStore } from '@/modules/auth/stores/PermissionStore'
import { useUserStore } from '@/modules/user/stores/UserStore'
import SpaceVariable from '@/modules/space/models/SpaceVariable'
import { isVariableSchemaLocked } from '@/util/variable-schema-lock'
import { buildAssignCalloutAttributes, getAvatarColorIndex, getAvatarInitial } from '@/util/variable-callout-utils'
import {
  getIconStateFromFlags,
  getVariableIconState,
  getVariableValueFlags,
  type VariableIconState
} from '@/util/variable-state'

// Variable name validation: non-empty, alphanumeric and underscore only
const VARIABLE_NAME_REGEX = /^[a-zA-Z0-9_]+$/

// Pre-compiled regex patterns for better performance
const VARIABLE_TEXT_PATTERN = /\{\{\s*([^}]+?)\s*\}\}/

type IconState = VariableIconState

// Legacy icon prefixes that may exist in older content
const LEGACY_ICON_WARN = '⚠️'
const LEGACY_ICON_PEND = '⏳'
const LEGACY_ICON_PREFIXES = [LEGACY_ICON_WARN, LEGACY_ICON_PEND]

// Performance constants
const CHECK_INTERVAL = 2000 // Check every 2 seconds at most
const MIN_UPDATE_INTERVAL = 100 // Minimum interval between updates

/**
 * Creates a normalized variable name map for O(1) lookups
 * @param variables - Array of SpaceVariable objects
 * @returns Map of normalized variable names to SpaceVariable objects
 */
function createVariableMap(variables: SpaceVariable[]): Map<string, SpaceVariable> {
  const map = new Map<string, SpaceVariable>()
  for (let i = 0; i < variables.length; i++) {
    const variable = variables[i]
    map.set(variable.variableName.toLowerCase(), variable)
  }
  return map
}

/**
 * Gets variable state information by looking it up in SpaceVariableStore
 * @param variableName - The name of the variable to check
 * @returns Object with hasDefault, hasRecipient boolean flags and recipientEmail
 */
function findVariableById(variableId?: string | null): SpaceVariable | undefined {
  if (!variableId) return undefined
  const spaceVariableStore = useSpaceVariableStore()
  for (const variables of spaceVariableStore.spaceVariables.values()) {
    const found = variables.find(v => v.id === variableId)
    if (found) return found
  }
  return undefined
}

function findVariableByName(variableName?: string | null, scopeId?: string | null): SpaceVariable | undefined {
  if (!variableName) return undefined
  const spaceStore = useSpaceStore()
  const spaceVariableStore = useSpaceVariableStore()
  const spaceId = scopeId || spaceStore.currentSpaceId
  const normalizedName = variableName.toLowerCase()

  if (spaceId) {
    const scoped = spaceVariableStore.getVariableByName(spaceId, variableName)
    if (scoped) return scoped
  }

  for (const variables of spaceVariableStore.spaceVariables.values()) {
    const found = variables.find(v => v.variableName.toLowerCase() === normalizedName)
    if (found) return found
  }
  return undefined
}

function getAssignedToCurrentUser(variable: SpaceVariable): boolean | null {
  const appStore = useAppStore()
  const permissionStore = usePermissionStore()
  const isInPreviewOrExternal = appStore.isInPreviewMode || permissionStore.isExternalUser()
  if (!isInPreviewOrExternal) {
    return null
  }

  const currentUser = useUserStore().currentUser
  const assignedRecipientId = variable.assignedToRecipientId?.trim()
  const assignedRecipientName = variable.assignedToRecipientName?.trim()
  if (!currentUser || (!assignedRecipientId && !assignedRecipientName)) {
    return null
  }

  const matchesId = !!(assignedRecipientId && currentUser.id && assignedRecipientId === currentUser.id)
  const matchesEmail = !!(
    assignedRecipientName &&
    currentUser.email &&
    assignedRecipientName.toLowerCase() === currentUser.email.toLowerCase()
  )
  return matchesId || matchesEmail
}

function getAssignedToCurrentUserAttr(variable: SpaceVariable): string | null {
  return getAssignedToCurrentUser(variable) === false ? 'false' : null
}

function getVariableState(
  variableName: string,
  variableId?: string | null,
  scopeId?: string | null
): {
  hasDefault: boolean
  hasRecipient: boolean
  hasRecipientValue?: boolean
  recipientEmail?: string
  assignedToCurrentUser?: boolean | null
} {
  const variableObj = findVariableById(variableId) || findVariableByName(variableName, scopeId)

  if (!variableObj) {
    return { hasDefault: false, hasRecipient: false, hasRecipientValue: false }
  }

  const { hasDefault, hasRecipient, hasRecipientValue } = getVariableValueFlags(variableObj)

  // Extract recipient email/name if available
  const recipientName = variableObj.assignedToRecipientName
  const recipientEmail = recipientName && recipientName.trim() ? recipientName.trim() : undefined

  const assignedToCurrentUser = getAssignedToCurrentUser(variableObj)

  return { hasDefault, hasRecipient, hasRecipientValue, recipientEmail, assignedToCurrentUser }
}

function stripLegacyIconPrefix(text: string): string {
  if (!text) return text
  for (let i = 0; i < LEGACY_ICON_PREFIXES.length; i++) {
    const icon = LEGACY_ICON_PREFIXES[i]
    if (text.startsWith(icon)) {
      return text.slice(icon.length)
    }
  }
  return text
}

function buildPlaceholderText(variableName: string): string {
  return `{{ ${variableName} }}`
}

function isTemplateContext(scopeId?: string | null): boolean {
  if (!scopeId) {
    return false
  }
  const spaceStore = useSpaceStore()
  return scopeId !== spaceStore.currentSpaceId
}

export const VariablePlaceholder = Mark.create({
  name: 'variablePlaceholder',

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'variable-placeholder'
      },
      exitable: true,
      scopeId: null as string | null
    }
  },

  addAttributes() {
    return {
      'data-has-default': {
        default: null,
        parseHTML: element => element.getAttribute('data-has-default'),
        renderHTML: attributes => {
          if (!attributes['data-has-default']) {
            return {}
          }
          return {
            'data-has-default': attributes['data-has-default']
          }
        }
      },
      'data-has-recipient': {
        default: null,
        parseHTML: element => element.getAttribute('data-has-recipient'),
        renderHTML: attributes => {
          if (!attributes['data-has-recipient']) {
            return {}
          }
          return {
            'data-has-recipient': attributes['data-has-recipient']
          }
        }
      },
      'data-assigned-to-current-user': {
        default: null,
        parseHTML: element => element.getAttribute('data-assigned-to-current-user'),
        renderHTML: attributes => {
          if (!attributes['data-assigned-to-current-user']) {
            return {}
          }
          return {
            'data-assigned-to-current-user': attributes['data-assigned-to-current-user']
          }
        }
      },
      'data-variable-id': {
        default: null,
        parseHTML: element => element.getAttribute('data-variable-id'),
        renderHTML: attributes => {
          if (!attributes['data-variable-id']) {
            return {}
          }
          return {
            'data-variable-id': attributes['data-variable-id']
          }
        }
      },
      'data-variable-name': {
        default: null,
        parseHTML: element => element.getAttribute('data-variable-name'),
        renderHTML: attributes => {
          if (!attributes['data-variable-name']) {
            return {}
          }
          return {
            'data-variable-name': attributes['data-variable-name']
          }
        }
      },
      'data-icon-state': {
        default: null,
        parseHTML: element => element.getAttribute('data-icon-state'),
        renderHTML: attributes => {
          if (!attributes['data-icon-state']) {
            return {}
          }
          return {
            'data-icon-state': attributes['data-icon-state']
          }
        }
      },
      'data-avatar-color-index': {
        default: null,
        parseHTML: element => element.getAttribute('data-avatar-color-index'),
        renderHTML: attributes => {
          if (!attributes['data-avatar-color-index']) {
            return {}
          }
          return {
            'data-avatar-color-index': attributes['data-avatar-color-index']
          }
        }
      },
      'data-avatar-letter': {
        default: null,
        parseHTML: element => element.getAttribute('data-avatar-letter'),
        renderHTML: attributes => {
          if (!attributes['data-avatar-letter']) {
            return {}
          }
          return {
            'data-avatar-letter': attributes['data-avatar-letter']
          }
        }
      }
    }
  },

  parseHTML() {
    const scopeId = this.options.scopeId || null
    return [
      {
        tag: 'span',
        getAttrs: (node: string | HTMLElement) => {
          if (typeof node === 'string') return false
          const hasClass = node.classList?.contains('variable-placeholder')
          if (hasClass) {
            // Parse both editable and non-editable variables
            // Works for both direct variable-placeholder spans and nested ones inside variable-wrapper
            // Parse data attributes
            const hasDefault = node.getAttribute('data-has-default')
            const hasRecipient = node.getAttribute('data-has-recipient')
            const variableId = node.getAttribute('data-variable-id')
            let variableName = node.getAttribute('data-variable-name')

            // If variableName is not in attributes, try to extract from text content
            if (!variableName) {
              const textContent = node.textContent || ''
              const cleanedText = stripLegacyIconPrefix(textContent).trim()
              const match = cleanedText.match(VARIABLE_TEXT_PATTERN)
              if (match && match[1]) {
                variableName = match[1].trim()
              }
            }

            // Check store for current variable state and update attributes if needed
            // Only check store if variableName is available to avoid unnecessary lookups
            if (variableName) {
              const state = getVariableState(variableName, variableId, scopeId)
              // Override with store state if available
              const iconState = getIconStateFromFlags(state.hasDefault, state.hasRecipient, state.hasRecipientValue)
              const avatarLetter =
                iconState === 'pend' && state.recipientEmail ? getAvatarInitial(undefined, state.recipientEmail) : null
              const avatarColorIndex =
                iconState === 'pend' && state.recipientEmail
                  ? getAvatarColorIndex(state.recipientEmail).toString()
                  : null
              const assignedToCurrentUser = state.assignedToCurrentUser === false ? 'false' : null
              const attrs = {
                'data-has-default': state.hasDefault ? 'true' : 'false',
                'data-has-recipient': state.hasRecipient ? 'true' : null,
                'data-assigned-to-current-user': assignedToCurrentUser,
                'data-variable-id': variableId || null,
                'data-variable-name': variableName || null,
                'data-icon-state': iconState,
                'data-avatar-letter': avatarLetter,
                'data-avatar-color-index': avatarColorIndex
              }
              return attrs
            }

            // Return HTML attributes if variableName is not available
            return {
              'data-has-default': hasDefault || null,
              'data-has-recipient': hasRecipient || null,
              'data-assigned-to-current-user': node.getAttribute('data-assigned-to-current-user') || null,
              'data-variable-id': variableId || null,
              'data-variable-name': variableName || null,
              'data-icon-state': node.getAttribute('data-icon-state') || null,
              'data-avatar-letter': node.getAttribute('data-avatar-letter') || null,
              'data-avatar-color-index': node.getAttribute('data-avatar-color-index') || null
            }
          }
          return false
        }
      },
      {
        // Also support finding variable-placeholder when nested inside variable-wrapper
        tag: 'span.variable-wrapper span.variable-placeholder',
        getAttrs: (node: string | HTMLElement) => {
          if (typeof node === 'string') return false
          const hasDefault = node.getAttribute('data-has-default')
          const hasRecipient = node.getAttribute('data-has-recipient')
          const variableId = node.getAttribute('data-variable-id')
          let variableName = node.getAttribute('data-variable-name')

          // If variableName is not in attributes, try to extract from text content
          if (!variableName) {
            const textContent = node.textContent || ''
            const cleanedText = stripLegacyIconPrefix(textContent).trim()
            const match = cleanedText.match(VARIABLE_TEXT_PATTERN)
            if (match && match[1]) {
              variableName = match[1].trim()
            }
          }

          // Check store for current variable state and update attributes if needed
          // Only check store if variableName is available to avoid unnecessary lookups
          if (variableName) {
            const state = getVariableState(variableName, variableId, scopeId)
            // Override with store state if available
            const iconState = getIconStateFromFlags(state.hasDefault, state.hasRecipient, state.hasRecipientValue)
            const avatarLetter =
              iconState === 'pend' && state.recipientEmail ? getAvatarInitial(undefined, state.recipientEmail) : null
            const avatarColorIndex =
              iconState === 'pend' && state.recipientEmail ? getAvatarColorIndex(state.recipientEmail).toString() : null
            const assignedToCurrentUser = state.assignedToCurrentUser === false ? 'false' : null
            return {
              'data-has-default': state.hasDefault ? 'true' : 'false',
              'data-has-recipient': state.hasRecipient && !state.hasDefault ? 'true' : null,
              'data-assigned-to-current-user': assignedToCurrentUser,
              'data-variable-id': variableId || null,
              'data-variable-name': variableName || null,
              'data-icon-state': iconState,
              'data-avatar-letter': avatarLetter,
              'data-avatar-color-index': avatarColorIndex
            }
          }

          return {
            'data-has-default': hasDefault || null,
            'data-has-recipient': hasRecipient || null,
            'data-assigned-to-current-user': node.getAttribute('data-assigned-to-current-user') || null,
            'data-variable-id': variableId || null,
            'data-variable-name': variableName || null,
            'data-icon-state': node.getAttribute('data-icon-state') || null,
            'data-avatar-letter': node.getAttribute('data-avatar-letter') || null,
            'data-avatar-color-index': node.getAttribute('data-avatar-color-index') || null
          }
        }
      }
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)
    const hasDefault = attrs['data-has-default'] === 'true'
    const hasRecipient = attrs['data-has-recipient'] === 'true'
    let iconState = (attrs['data-icon-state'] as string | null) || null
    const hasIdentifier = !!attrs['data-variable-id'] || !!attrs['data-variable-name']

    // Fallback for older content missing data-icon-state
    if (!iconState && !hasDefault && hasIdentifier) {
      iconState = hasRecipient ? 'pend' : 'warn'
    }

    if (iconState) {
      const iconClass = `icon-${iconState}`
      attrs.class = attrs.class ? `${attrs.class} ${iconClass}` : iconClass
    }

    if (iconState !== 'pend' && attrs['data-avatar-letter']) {
      delete attrs['data-avatar-letter']
    }
    if (iconState !== 'pend' && attrs['data-avatar-color-index']) {
      delete attrs['data-avatar-color-index']
    }

    return ['span', attrs, 0]
  },

  addKeyboardShortcuts() {
    return {
      ArrowRight: () => {
        return this.editor.commands.exitCode()
      },
      Backspace: () => {
        const { state, view } = this.editor
        const selection = state.selection
        const { $from, empty } = selection
        if (!empty) {
          return false
        }

        const pos = $from.pos
        const docSize = state.doc.content.size

        // Validate position is within document bounds
        if (pos < 0 || pos > docSize) {
          return false
        }

        // Check if cursor is at the start of document
        if (pos === 0) {
          return false
        }

        // Early exit using document state only: if cursor is not in or adjacent to a variable,
        // allow default backspace. This avoids DOM-based detection which can be out of sync
        // in production (e.g. Vue batched updates), causing backspace to fail after typing.
        const resolved = state.doc.resolve(pos)
        const hasVariableMarkAtCursor =
          resolved.marks().some(mark => mark.type.name === 'variablePlaceholder') ||
          (resolved.nodeBefore?.marks?.some(mark => mark.type.name === 'variablePlaceholder') ?? false)
        // Cursor right after variableWrapper (e.g. with assign-callout): nodeBefore is the wrapper node (no mark)
        const hasVariableWrapperBefore = resolved.nodeBefore?.type?.name === 'variableWrapper'
        if (!hasVariableMarkAtCursor && !hasVariableWrapperBefore) {
          return false
        }

        // Get the DOM node at the cursor position
        const domNode = view.domAtPos(pos)
        if (!domNode || !domNode.node) {
          return false
        }

        // Find the variable placeholder or wrapper span
        let variableSpan: HTMLElement | null = null
        let variableWrapper: HTMLElement | null = null
        let currentNode: Node | null = domNode.node

        // Traverse up the DOM tree to find the variable-placeholder or variable-wrapper span
        while (currentNode && currentNode !== view.dom) {
          if (currentNode.nodeType === Node.ELEMENT_NODE) {
            const element = currentNode as HTMLElement
            if (element.classList && element.classList.contains('variable-wrapper')) {
              variableWrapper = element
              break
            }
            if (element.classList && element.classList.contains('variable-placeholder')) {
              variableSpan = element
              // Continue searching for wrapper
              if (!variableWrapper) {
                const wrapper = element.closest('.variable-wrapper') as HTMLElement
                if (wrapper) {
                  variableWrapper = wrapper
                }
              }
            }
          }
          currentNode = currentNode.parentNode
        }

        // Use variable-wrapper if it exists, otherwise use variable-placeholder (backward compatibility)
        const targetSpan = variableWrapper || variableSpan

        // If cursor is outside the target node range, allow normal backspace
        if (targetSpan) {
          const targetStart = view.posAtDOM(targetSpan, 0)
          if (targetStart !== null && targetStart !== -1) {
            const targetNode = state.doc.nodeAt(targetStart)
            if (targetNode) {
              let targetEnd = targetStart + targetNode.nodeSize
              const nodeAfter = state.doc.nodeAt(targetEnd)
              if (nodeAfter && nodeAfter.type.name === 'caretAnchor') {
                targetEnd += nodeAfter.nodeSize
              }
              if (pos < targetStart || pos >= targetEnd) {
                return false
              }
            }
          }
        }

        // Scenario 1: If cursor is inside variable, handle backspace based on editability
        if (targetSpan && variableSpan) {
          // Check if it's an editable variable (new variable being created)
          // Fallback: treat placeholders without persistent binding as editable
          const hasPersistentBinding = !!(
            variableSpan.getAttribute('data-variable-id') || variableSpan.getAttribute('data-variable-name')
          )
          const isEditable = variableSpan.getAttribute('data-variable-editable') === 'true' || !hasPersistentBinding

          // Check if variable is empty (regardless of editable state)
          // Empty variables should be deleted when backspace is pressed
          const spanStart = view.posAtDOM(targetSpan, 0)
          if (spanStart !== null && spanStart !== -1) {
            // Find the text node with variablePlaceholder mark in document state
            let variableTextNode: any = null

            state.doc.nodesBetween(spanStart, Math.min(spanStart + 50, docSize), (node, pos) => {
              if (node.isText && node.text) {
                const hasVariableMark = node.marks.some(mark => mark.type.name === 'variablePlaceholder')
                if (hasVariableMark && pos >= spanStart && pos <= spanStart + 50) {
                  variableTextNode = node
                  return false // Stop searching
                }
              }
            })

            if (variableTextNode && variableTextNode.text) {
              const variableText = variableTextNode.text

              // Check if variable is already empty
              if (isVariableEmpty(variableText)) {
                if (deleteEntireVariable(view, targetSpan, spanStart, docSize)) {
                  return true
                }
              }

              if (!isEditable) {
                // Persisted/non-editable variable: delete whole placeholder when backspacing inside it.
                const resolvedPos = state.doc.resolve(pos)
                const hasVariableMark =
                  resolvedPos.marks().some(mark => mark.type.name === 'variablePlaceholder') ||
                  (resolvedPos.nodeBefore?.marks || []).some(mark => mark.type.name === 'variablePlaceholder')
                if (hasVariableMark) {
                  if (deleteEntireVariable(view, targetSpan, spanStart, docSize)) {
                    return true
                  }
                }
              }

              // Editable variable: if it still has content, allow normal backspace (delete one character).
              // Whole-placeholder deletion is handled only when it's already empty (see check above).
            }
            // Allow normal backspace for editing if variable has content
            return false
          }

          // For non-editable variables, check if cursor is at the start of the variable
          // If at start, delete entire variable; otherwise allow normal backspace
          // Reuse spanStart that was already calculated above
          if (spanStart !== null && spanStart !== -1) {
            // Only delete entire variable if cursor is at the very start
            // This prevents accidental deletion when editing
            if (pos === spanStart) {
              // Calculate end position including caret anchor
              const textContent = targetSpan.textContent || ''
              let spanEnd = spanStart + textContent.length

              // Check for caret-anchor span after variable
              const nextSibling = targetSpan.nextSibling
              if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE) {
                const nextText = nextSibling.textContent || ''
                if (nextText.startsWith('\u200B')) {
                  spanEnd += 1
                }
              } else if (nextSibling && (nextSibling as HTMLElement).classList?.contains('caret-anchor')) {
                // Check for caret-anchor span element
                const caretAnchorStart = view.posAtDOM(nextSibling, 0)
                if (caretAnchorStart !== null && caretAnchorStart !== -1) {
                  spanEnd = caretAnchorStart + 1 // Include caret anchor
                }
              }

              // Ensure spanEnd doesn't exceed parent paragraph boundary
              const resolvedPos = state.doc.resolve(spanStart)
              const parentNode = resolvedPos.parent
              const parentStart = resolvedPos.start(resolvedPos.depth)
              const parentEnd = parentStart + parentNode.nodeSize

              if (spanEnd > parentEnd) {
                spanEnd = parentEnd
              }

              // Validate positions before deleting
              if (spanStart >= 0 && spanEnd <= docSize && spanStart < spanEnd && spanEnd <= parentEnd) {
                this.editor
                  .chain()
                  .focus()
                  .deleteRange({ from: spanStart, to: spanEnd })
                  .setTextSelection(spanStart)
                  .run()
                return true
              }
            }
          }
          // If cursor is inside variable but not at start, allow normal backspace
          return false
        }

        // Scenario 2: Check if cursor is right after a variable (check previous position)
        // This deletes the entire variable when backspace is pressed after it
        const prevPos = pos - 1
        if (prevPos >= 0) {
          const prevDomNode = view.domAtPos(prevPos)
          if (prevDomNode && prevDomNode.node) {
            let prevVariableSpan: HTMLElement | null = null
            let prevVariableWrapper: HTMLElement | null = null
            let prevCurrentNode: Node | null = prevDomNode.node

            while (prevCurrentNode && prevCurrentNode !== view.dom) {
              if (prevCurrentNode.nodeType === Node.ELEMENT_NODE) {
                const element = prevCurrentNode as HTMLElement
                if (element.classList && element.classList.contains('variable-wrapper')) {
                  prevVariableWrapper = element
                  break
                }
                if (element.classList && element.classList.contains('variable-placeholder')) {
                  prevVariableSpan = element
                  // Continue searching for wrapper
                  if (!prevVariableWrapper) {
                    const wrapper = element.closest('.variable-wrapper') as HTMLElement
                    if (wrapper) {
                      prevVariableWrapper = wrapper
                    }
                  }
                }
              }
              prevCurrentNode = prevCurrentNode.parentNode
            }

            // Use variable-wrapper if it exists, otherwise use variable-placeholder (backward compatibility)
            const prevTargetSpan = prevVariableWrapper || prevVariableSpan

            if (prevTargetSpan) {
              const prevSpanStart = view.posAtDOM(prevTargetSpan, 0)
              if (prevSpanStart !== null && prevSpanStart !== -1) {
                if (deleteEntireVariable(view, prevTargetSpan, prevSpanStart, docSize)) {
                  return true
                }
                const prevTextContent = prevTargetSpan.textContent || ''
                let prevSpanEnd = prevSpanStart + prevTextContent.length

                // Check for caret-anchor span after variable
                const prevNextSibling = prevTargetSpan.nextSibling
                if (prevNextSibling && prevNextSibling.nodeType === Node.TEXT_NODE) {
                  const prevNextText = prevNextSibling.textContent || ''
                  if (prevNextText.startsWith('\u200B')) {
                    prevSpanEnd += 1
                  }
                } else if (prevNextSibling && (prevNextSibling as HTMLElement).classList?.contains('caret-anchor')) {
                  // Check for caret-anchor span element
                  const caretAnchorStart = view.posAtDOM(prevNextSibling, 0)
                  if (caretAnchorStart !== null && caretAnchorStart !== -1) {
                    prevSpanEnd = caretAnchorStart + 1
                  }
                }

                // Ensure prevSpanEnd doesn't exceed parent paragraph boundary
                const prevResolvedPos = state.doc.resolve(prevSpanStart)
                const prevParentNode = prevResolvedPos.parent
                const prevParentStart = prevResolvedPos.start(prevResolvedPos.depth)
                const prevParentEnd = prevParentStart + prevParentNode.nodeSize

                if (prevSpanEnd > prevParentEnd) {
                  prevSpanEnd = prevParentEnd
                }

                if (
                  prevSpanStart >= 0 &&
                  prevSpanEnd <= docSize &&
                  prevSpanStart < prevSpanEnd &&
                  prevSpanEnd <= prevParentEnd
                ) {
                  this.editor
                    .chain()
                    .focus()
                    .deleteRange({ from: prevSpanStart, to: prevSpanEnd })
                    .setTextSelection(prevSpanStart)
                    .run()
                  return true
                }
              }
            }
          }
        }

        return false
      }
    }
  },

  addProseMirrorPlugins() {
    const editor = this.editor
    const scopeId = this.options.scopeId || null

    return [
      new Plugin({
        key: new PluginKey('variablePlaceholderPaste'),
        props: {
          handlePaste: (view, _event, slice) => {
            const { state } = view
            const { selection, schema } = state
            const variablePlaceholderMark = schema.marks.variablePlaceholder
            if (!variablePlaceholderMark) {
              return false
            }

            const docSize = state.doc.content.size
            const clampPos = (pos: number): number => Math.max(0, Math.min(pos, docSize))
            const hasMarkAtPos = (pos: number): boolean => {
              const $pos = state.doc.resolve(clampPos(pos))
              return $pos.marks().some(mark => mark.type === variablePlaceholderMark)
            }

            const isInsideVariable = selection.empty
              ? hasMarkAtPos(selection.from) ||
                hasMarkAtPos(Math.max(0, selection.from - 1)) ||
                hasMarkAtPos(Math.min(docSize, selection.from + 1))
              : state.doc.rangeHasMark(selection.from, selection.to, variablePlaceholderMark)

            if (!isInsideVariable) {
              return false
            }

            const currentMark =
              selection.$from.marks().find(mark => mark.type === variablePlaceholderMark) ||
              selection.$to.marks().find(mark => mark.type === variablePlaceholderMark)
            if (!currentMark) {
              return false
            }

            const markToApply = variablePlaceholderMark.create(currentMark.attrs || {})
            const applyMarkToNode = (node: any): any => {
              if (node.isText) {
                return node.mark(markToApply.addToSet(node.marks))
              }
              if (node.content && node.content.size > 0) {
                const mappedChildren: any[] = []
                node.content.forEach((child: any) => {
                  mappedChildren.push(applyMarkToNode(child))
                })
                return node.copy(Fragment.fromArray(mappedChildren))
              }
              return node
            }

            const mappedNodes: any[] = []
            slice.content.forEach(node => {
              mappedNodes.push(applyMarkToNode(node))
            })
            const mappedContent = Fragment.fromArray(mappedNodes)
            const transformedSlice = new Slice(mappedContent, slice.openStart, slice.openEnd)
            const tr = state.tr.replaceSelection(transformedSlice)
            view.dispatch(tr.scrollIntoView())
            return true
          }
        }
      }),
      new Plugin({
        key: new PluginKey('variablePlaceholderEditable'),
        props: {
          handleDOMEvents: {
            blur: (view, event) => {
              const target = event.target as HTMLElement
              if (target.classList.contains('variable-inner')) {
                validateAndFinalizeVariable(target, view, editor, !isTemplateContext(scopeId))
              }
              return false
            },
            keydown: (view, event) => {
              const target = event.target as HTMLElement
              if (target.classList.contains('variable-inner')) {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  event.stopPropagation()
                  validateAndFinalizeVariable(target, view, editor, !isTemplateContext(scopeId))
                  return true
                }
              }
              return false
            }
          }
        }
      }),
      // Plugin to update variable marks when document loads or store changes
      new Plugin({
        key: new PluginKey('variablePlaceholderUpdateOnLoad'),
        view: view => {
          let lastStoreCheck = 0
          let lastVariablesHash = ''
          let isUpdating = false // Guard flag to prevent infinite loops
          let variableMapCache: Map<string, SpaceVariable> | null = null
          let lastSpaceId: string | null = null
          let intervalId: number | null = null
          let lastVariableKeys = new Set<string>()
          let hasInitializedKeys = false

          const syncFromStore = (view: any, prevState: any) => {
            // Prevent re-entry during updates
            if (isUpdating) {
              return
            }

            const now = Date.now()
            const docChanged = !prevState.doc.eq(view.state.doc)

            // Skip if document hasn't changed and not enough time has passed
            if (!docChanged) {
              if (now - lastStoreCheck < CHECK_INTERVAL) {
                return
              }
            } else {
              // Document changed - use shorter interval but still respect minimum
              if (now - lastStoreCheck < MIN_UPDATE_INTERVAL) {
                return
              }
            }

            lastStoreCheck = now

            const { doc } = view.state
            const spaceVariableStore = useSpaceVariableStore()
            const spaceStore = useSpaceStore()
            const spaceId = scopeId || spaceStore.currentSpaceId

            if (!spaceId) {
              return
            }

            // Invalidate cache if space changed
            if (lastSpaceId !== spaceId) {
              variableMapCache = null
              lastSpaceId = spaceId
            }

            const variables = spaceVariableStore.getVariablesBySpaceId(spaceId)
            if (variables.length === 0) {
              variableMapCache = null
              return
            }

            // Create hash for change detection (optimized)
            // Must include assignedToRecipientId to detect recipient assignment changes
            let variablesHash = ''
            if (variables.length <= 50) {
              // Only hash for reasonable sizes
              for (let i = 0; i < variables.length; i++) {
                const v = variables[i]
                variablesHash += `${v.id}:${v.defaultValue || ''}:${v.recipientValue || ''}:${
                  v.assignedToRecipientId || ''
                }|`
              }
            } else {
              // For large variable sets, use a simpler hash but still include recipient assignment
              const firstVar = variables[0]
              variablesHash = `${variables.length}:${firstVar?.id || ''}:${firstVar?.assignedToRecipientId || ''}`
            }

            // Skip if variables haven't changed and document hasn't changed
            if (variablesHash === lastVariablesHash && !docChanged) {
              return
            }

            // Refresh cache whenever variables change to avoid stale state
            if (variablesHash !== lastVariablesHash) {
              variableMapCache = createVariableMap(variables)
              lastVariablesHash = variablesHash
            } else if (!variableMapCache) {
              variableMapCache = createVariableMap(variables)
            }

            // Check if any variable marks need updating
            const variablePlaceholderMarkType = view.state.schema.marks.variablePlaceholder
            const variableWrapperNodeType = view.state.schema.nodes.variableWrapper
            const assignCalloutNodeType = view.state.schema.nodes.assignCallout
            const templateContext = isTemplateContext(scopeId)
            if (!variablePlaceholderMarkType) {
              return
            }

            if (docChanged || !hasInitializedKeys) {
              const currentVariableKeys = new Set<string>()
              const instanceCounts: Record<string, number> = {}
              doc.descendants((node: any) => {
                if (!node.isText || !node.marks) {
                  return true
                }
                const mark = node.marks.find((m: any) => m.type === variablePlaceholderMarkType)
                if (!mark) {
                  return true
                }
                const variableId = mark.attrs['data-variable-id']
                const variableName = mark.attrs['data-variable-name']
                const key = variableId
                  ? `id:${variableId}`
                  : variableName
                  ? `name:${variableName.toLowerCase()}`
                  : 'unknown'
                instanceCounts[key] = (instanceCounts[key] || 0) + 1
                if (variableId) {
                  currentVariableKeys.add(`id:${variableId}`)
                } else if (variableName) {
                  currentVariableKeys.add(`name:${variableName.toLowerCase()}`)
                }
                return true
              })
              if (hasInitializedKeys && currentVariableKeys.size < lastVariableKeys.size) {
                for (const key of lastVariableKeys) {
                  if (!currentVariableKeys.has(key)) {
                    let variableToUpdate: SpaceVariable | undefined
                    if (key.startsWith('id:')) {
                      const variableId = key.slice(3)
                      variableToUpdate = spaceVariableStore.getVariableById(spaceId, variableId)
                    } else if (key.startsWith('name:')) {
                      const variableName = key.slice(5)
                      variableToUpdate = spaceVariableStore.getVariableByName(spaceId, variableName)
                    }

                    const domCount = (() => {
                      const root = view.dom?.ownerDocument
                      if (!root) return null
                      if (key.startsWith('id:')) {
                        return root.querySelectorAll(`.variable-placeholder[data-variable-id="${key.slice(3)}"]`).length
                      }
                      return root.querySelectorAll(`.variable-placeholder[data-variable-name="${key.slice(5)}"]`).length
                    })()

                    const appStore = useAppStore()
                    const permissionStore = usePermissionStore()
                    const isInPreviewOrExternal = appStore.isInPreviewMode || permissionStore.isExternalUser()
                    const isTemplateScope = !!scopeId && scopeId !== spaceStore.currentSpaceId
                    const canUpdateVariable =
                      !!editor?.isEditable && !isInPreviewOrExternal && (isTemplateScope || !isVariableSchemaLocked())
                    if (
                      canUpdateVariable &&
                      domCount === 0 &&
                      variableToUpdate &&
                      (variableToUpdate.recipientValue?.trim() || variableToUpdate.assignedToRecipientId)
                    ) {
                      spaceVariableStore.updateVariable(spaceId, variableToUpdate.id, {
                        variableName: variableToUpdate.variableName,
                        displayLabel: variableToUpdate.displayLabel,
                        variableType: variableToUpdate.variableType,
                        defaultValue: variableToUpdate.defaultValue || undefined,
                        dateFormat: variableToUpdate.dateFormat,
                        options: variableToUpdate.options
                      })
                    }
                  }
                }
              }

              lastVariableKeys = currentVariableKeys
              hasInitializedKeys = true
            }

            let needsUpdate = false
            const missingVariables = new Set<string>()
            const updates: Array<{
              from: number
              to: number
              attrs: Record<string, string | null>
              newText: string
            }> = []
            const wrapperDeletes: Array<{ from: number; to: number }> = []
            const calloutDeletes: Array<{ from: number; to: number }> = []
            const calloutInserts: Array<{ pos: number; node: any }> = []
            const calloutUpdates: Array<{ from: number; to: number; node: any }> = []
            const wrapperInfo = new Map<
              number,
              {
                hasDefault: boolean
                hasRecipient: boolean
                assignedToCurrentUser?: boolean | null
                variable?: SpaceVariable
                variableName: string
                iconState: IconState | null
              }
            >()
            const wrapperTextByPos = new Map<number, string>()

            // Traverse document once, collecting all updates
            doc.descendants((node: any, pos: number) => {
              // Early return for non-text nodes
              if (!node.isText || !node.marks) {
                return true
              }

              // Fast mark lookup
              let mark = null
              const marks = node.marks
              for (let i = 0; i < marks.length; i++) {
                if (marks[i].type === variablePlaceholderMarkType) {
                  mark = marks[i]
                  break
                }
              }

              if (!mark) {
                return true
              }

              const variableId = mark.attrs['data-variable-id']
              let variableName = mark.attrs['data-variable-name']
              let variableById: SpaceVariable | undefined

              if (!variableName && variableId) {
                variableById = spaceVariableStore.getVariableById(spaceId, variableId)
                if (variableById?.variableName) {
                  variableName = variableById.variableName
                }
              }

              if (!variableName) {
                return true
              }

              // O(1) lookup using cached map (fallback to id lookup if needed)
              const variable = variableMapCache!.get(variableName.toLowerCase()) || variableById
              if (!variable) {
                missingVariables.add(variableName)
                return true
              }

              // Compute state once
              const { hasDefault, hasRecipient } = getVariableValueFlags(variable)

              const newIconState = getVariableIconState(variable)
              const avatarLetter =
                newIconState === 'pend' && variable.assignedToRecipientName
                  ? getAvatarInitial(undefined, variable.assignedToRecipientName)
                  : null
              const avatarColorIndex =
                newIconState === 'pend' && variable.assignedToRecipientName
                  ? getAvatarColorIndex(variable.assignedToRecipientName).toString()
                  : null

              // Get current text and remove legacy icon prefix if present
              const currentText = node.text || ''
              const cleanedText = stripLegacyIconPrefix(currentText)
              const canonicalText = buildPlaceholderText(variableName)
              const textNeedsUpdate = cleanedText !== canonicalText

              // Check if mark attributes need updating
              const currentHasDefault = mark.attrs['data-has-default'] === 'true'
              const currentHasRecipient = mark.attrs['data-has-recipient'] === 'true'
              const expectedHasRecipient = hasRecipient
              const currentAssignedToCurrentUser =
                (mark.attrs['data-assigned-to-current-user'] as string | null) || null
              const expectedAssignedToCurrentUser = getAssignedToCurrentUserAttr(variable)
              // Update if default value changed OR recipient assignment changed (when no default)
              // Note: data-has-recipient is only set when hasRecipient is true AND hasDefault is false
              const currentIconState = (mark.attrs['data-icon-state'] as IconState) || null
              const currentVariableId = mark.attrs['data-variable-id']
              const expectedVariableId = variable.id || null
              const markNeedsUpdate =
                currentHasDefault !== hasDefault ||
                currentHasRecipient !== expectedHasRecipient ||
                currentAssignedToCurrentUser !== expectedAssignedToCurrentUser ||
                currentIconState !== newIconState ||
                (expectedVariableId && currentVariableId !== expectedVariableId)

              let wrapperPos: number | null = null
              if (variableWrapperNodeType) {
                const resolvedPos = view.state.doc.resolve(pos)
                for (let depth = resolvedPos.depth; depth > 0; depth--) {
                  if (resolvedPos.node(depth).type === variableWrapperNodeType) {
                    const resolvedWrapperPos = resolvedPos.before(depth)
                    wrapperPos = resolvedWrapperPos
                    wrapperInfo.set(resolvedWrapperPos, {
                      hasDefault,
                      hasRecipient,
                      assignedToCurrentUser: expectedAssignedToCurrentUser === 'false' ? false : null,
                      variable,
                      variableName,
                      iconState: newIconState
                    })
                    break
                  }
                }
              }

              if (wrapperPos !== null) {
                const existingText = wrapperTextByPos.get(wrapperPos) || ''
                wrapperTextByPos.set(wrapperPos, existingText + cleanedText)
              }

              // Only update if something actually changed
              if (markNeedsUpdate || textNeedsUpdate) {
                const newText = canonicalText

                // Only add to updates if text or mark actually changed
                if (newText !== currentText || markNeedsUpdate) {
                  needsUpdate = true
                  updates.push({
                    from: pos,
                    to: pos + node.nodeSize,
                    attrs: {
                      'data-has-default': hasDefault ? 'true' : 'false',
                      'data-has-recipient': hasRecipient ? 'true' : null,
                      'data-assigned-to-current-user': expectedAssignedToCurrentUser,
                      'data-variable-id': variable.id || mark.attrs['data-variable-id'] || null,
                      'data-variable-name': variableName,
                      'data-icon-state': newIconState,
                      'data-avatar-letter': avatarLetter,
                      'data-avatar-color-index': avatarColorIndex
                    },
                    newText: newText
                  })
                }
              }

              return true
            })

            if (missingVariables.size > 0 && editor) {
              missingVariables.forEach(name => {
                deleteAllPlaceholderInstances(editor, undefined, name, { focusAndSelect: false })
              })
              return
            }

            const wrapperReplacements: Array<{ from: number; to: number; nodes: any[]; pos: number }> = []
            const wrappersReplaced = new Set<number>()
            if (variableWrapperNodeType && wrapperTextByPos.size > 0) {
              wrapperTextByPos.forEach((wrapperText, pos) => {
                const info = wrapperInfo.get(pos)
                if (!info?.variableName || !info.variable) {
                  return
                }
                const canonicalText = buildPlaceholderText(info.variableName)
                if (wrapperText === canonicalText) {
                  return
                }

                const wrapperNode = doc.nodeAt(pos)
                if (!wrapperNode) {
                  return
                }

                const markAttributes: Record<string, string | null> = {
                  'data-has-default': info.hasDefault ? 'true' : 'false',
                  'data-has-recipient': info.hasRecipient ? 'true' : null,
                  'data-assigned-to-current-user': info.assignedToCurrentUser === false ? 'false' : null,
                  'data-variable-id': info.variable.id || null,
                  'data-variable-name': info.variableName,
                  'data-icon-state': info.iconState,
                  'data-avatar-letter':
                    info.iconState === 'pend' && info.variable.assignedToRecipientName
                      ? getAvatarInitial(undefined, info.variable.assignedToRecipientName)
                      : null,
                  'data-avatar-color-index':
                    info.iconState === 'pend' && info.variable.assignedToRecipientName
                      ? getAvatarColorIndex(info.variable.assignedToRecipientName).toString()
                      : null
                }

                const nodesToInsert: any[] = []
                nodesToInsert.push(
                  view.state.schema.text(canonicalText, [variablePlaceholderMarkType.create(markAttributes)])
                )

                if (assignCalloutNodeType && !templateContext) {
                  const recipientEmail = info.variable.assignedToRecipientName?.trim() || undefined
                  nodesToInsert.push(
                    assignCalloutNodeType.create(buildAssignCalloutAttributes(info.variable, recipientEmail))
                  )
                }

                const contentStart = pos + 1
                const contentEnd = pos + wrapperNode.content.size + 1
                wrapperReplacements.push({ from: contentStart, to: contentEnd, nodes: nodesToInsert, pos })
                wrappersReplaced.add(pos)
              })
            }

            if (wrappersReplaced.size > 0 && updates.length > 0 && wrapperReplacements.length > 0) {
              for (let i = updates.length - 1; i >= 0; i--) {
                const update = updates[i]
                let insideReplacement = false
                for (let j = 0; j < wrapperReplacements.length; j++) {
                  const range = wrapperReplacements[j]
                  if (update.from >= range.from && update.to <= range.to) {
                    insideReplacement = true
                    break
                  }
                }
                if (insideReplacement) {
                  updates.splice(i, 1)
                }
              }
            }

            if (assignCalloutNodeType && variableWrapperNodeType) {
              const shouldRenderCallouts = !!editor?.isEditable && !templateContext
              if (shouldRenderCallouts) {
                doc.descendants((node: any, pos: number) => {
                  if (node.type !== variableWrapperNodeType) {
                    return true
                  }

                  if (wrappersReplaced.has(pos)) {
                    return true
                  }

                  let hasVariablePlaceholder = false
                  node.forEach((child: any) => {
                    if (child.isText && child.marks) {
                      const hasMark = child.marks.some((mark: any) => mark.type === variablePlaceholderMarkType)
                      if (hasMark) {
                        hasVariablePlaceholder = true
                      }
                    }
                  })

                  if (!hasVariablePlaceholder) {
                    let deleteTo = pos + node.nodeSize
                    const nodeAfter = doc.nodeAt(deleteTo)
                    if (nodeAfter && nodeAfter.type === view.state.schema.nodes.caretAnchor) {
                      deleteTo += nodeAfter.nodeSize
                    }
                    wrapperDeletes.push({ from: pos, to: deleteTo })
                    return true
                  }

                  const info = wrapperInfo.get(pos)
                  const variable = info?.variable
                  const recipientEmail = variable?.assignedToRecipientName?.trim() || undefined
                  const desiredAttrs = buildAssignCalloutAttributes(variable, recipientEmail)

                  let hasCallout = false
                  node.forEach((child: any, offset: number) => {
                    if (child.type !== assignCalloutNodeType) {
                      return
                    }
                    hasCallout = true
                    const from = pos + offset + 1
                    const to = from + child.nodeSize
                    const currentEmail = child.attrs['data-recipient-email'] || null
                    const currentInitial = child.attrs['data-recipient-initial'] || null
                    const currentColor = child.attrs['data-recipient-color-index'] || null
                    const currentIsReassign = child.attrs['data-is-reassign'] === 'true'
                    const desiredEmail = desiredAttrs['data-recipient-email'] || null
                    const desiredInitial = desiredAttrs['data-recipient-initial'] || null
                    const desiredColor = desiredAttrs['data-recipient-color-index'] || null
                    const desiredIsReassign = desiredAttrs['data-is-reassign'] === 'true'

                    if (
                      currentEmail !== desiredEmail ||
                      currentInitial !== desiredInitial ||
                      currentColor !== desiredColor ||
                      currentIsReassign !== desiredIsReassign
                    ) {
                      calloutUpdates.push({
                        from,
                        to,
                        node: assignCalloutNodeType.create(desiredAttrs)
                      })
                    }
                  })

                  if (!hasCallout) {
                    const insertPos = pos + node.nodeSize - 1
                    calloutInserts.push({
                      pos: insertPos,
                      node: assignCalloutNodeType.create(desiredAttrs)
                    })
                  }

                  return true
                })
              } else if (templateContext) {
                doc.descendants((node: any, pos: number) => {
                  if (node.type === assignCalloutNodeType) {
                    calloutDeletes.push({ from: pos, to: pos + node.nodeSize })
                  }
                  return true
                })
              }
            }

            // Apply updates if needed
            if (
              (needsUpdate && updates.length > 0) ||
              wrapperDeletes.length > 0 ||
              calloutDeletes.length > 0 ||
              calloutUpdates.length > 0 ||
              calloutInserts.length > 0 ||
              wrapperReplacements.length > 0
            ) {
              // Double-check guard flag before proceeding
              if (isUpdating) {
                return
              }

              isUpdating = true // Set guard flag

              try {
                let tr = view.state.tr
                const docSize = doc.content.size

                if (wrapperDeletes.length > 0) {
                  wrapperDeletes.sort((a, b) => b.from - a.from)
                  for (const deletion of wrapperDeletes) {
                    if (deletion.from < 0 || deletion.to > docSize || deletion.from >= deletion.to) {
                      continue
                    }
                    tr = tr.delete(deletion.from, deletion.to)
                  }
                }

                const isInsideDeletedRange = (from: number, to?: number): boolean => {
                  for (let i = 0; i < wrapperDeletes.length; i++) {
                    const range = wrapperDeletes[i]
                    const end = to === undefined ? from : to
                    if (from >= range.from && end <= range.to) {
                      return true
                    }
                  }
                  return false
                }

                // Apply wrapper replacements next (reverse order)
                if (wrapperReplacements.length > 0) {
                  wrapperReplacements.sort((a, b) => b.from - a.from)
                  for (const replacement of wrapperReplacements) {
                    if (isInsideDeletedRange(replacement.from, replacement.to)) {
                      continue
                    }
                    if (replacement.from < 0 || replacement.to > docSize || replacement.from >= replacement.to) {
                      continue
                    }
                    tr = tr.replaceWith(replacement.from, replacement.to, replacement.nodes)
                  }
                }

                // Remove callouts first to avoid position shifts on text updates
                for (let i = calloutDeletes.length - 1; i >= 0; i--) {
                  const { from, to } = calloutDeletes[i]
                  if (isInsideDeletedRange(from, to)) {
                    continue
                  }
                  if (from < 0 || to > docSize || from >= to) {
                    continue
                  }
                  tr = tr.delete(from, to)
                }

                // Process callout attribute updates first to minimize position shifts
                for (let i = calloutUpdates.length - 1; i >= 0; i--) {
                  const { from, to, node } = calloutUpdates[i]
                  if (isInsideDeletedRange(from, to)) {
                    continue
                  }
                  if (from < 0 || to > docSize || from >= to) {
                    continue
                  }
                  tr = tr.replaceWith(from, to, node)
                }

                // Process updates in reverse order to maintain correct positions
                for (let i = updates.length - 1; i >= 0; i--) {
                  const { from, to, attrs, newText } = updates[i]
                  if (isInsideDeletedRange(from, to)) {
                    continue
                  }

                  // Validate positions are within document bounds (early skip)
                  if (from < 0 || to > docSize || from >= to) {
                    continue
                  }

                  // Batch operations: remove mark, delete, insert, add mark
                  tr = tr
                    .removeMark(from, to, variablePlaceholderMarkType)
                    .delete(from, to)
                    .insert(from, view.state.schema.text(newText))
                    .addMark(from, from + newText.length, variablePlaceholderMarkType.create(attrs))
                }

                // Insert callouts after text/mark updates
                for (let i = calloutInserts.length - 1; i >= 0; i--) {
                  const { pos, node } = calloutInserts[i]
                  if (isInsideDeletedRange(pos)) {
                    continue
                  }
                  if (pos < 0 || pos > docSize) {
                    continue
                  }
                  tr = tr.insert(pos, node)
                }

                // Only dispatch if transaction has changes
                if (tr.steps.length > 0) {
                  // Mark this transaction to prevent it from triggering our plugin again
                  tr.setMeta('variablePlaceholderUpdate', true)
                  tr.setMeta('addToHistory', false)
                  view.dispatch(tr)
                }
              } catch (error) {
                // Silently handle errors to avoid blocking editor updates
              } finally {
                // Reset guard flag after a microtask to ensure update cycle completes
                Promise.resolve().then(() => {
                  isUpdating = false
                })
              }
            }
          }

          // Run once on init and start periodic checks for store-only changes
          syncFromStore(view, view.state)
          intervalId = window.setInterval(() => {
            syncFromStore(view, view.state)
          }, CHECK_INTERVAL)

          return {
            update: (view, prevState) => {
              syncFromStore(view, prevState)
            },
            destroy: () => {
              if (intervalId !== null) {
                window.clearInterval(intervalId)
                intervalId = null
              }
            }
          }
        }
      })
    ]
  }
})

/**
 * Creates ProseMirror nodes for a variable with proper attributes and structure
 * @param schema - The ProseMirror schema
 * @param variableText - The formatted variable text (e.g., "{{ variableName }}")
 * @param hasDefault - Whether the variable has a default value
 * @param hasRecipient - Whether the variable has a recipient assignment
 * @param variableId - Optional variable ID from SpaceVariable
 * @param variableName - Variable name
 * @param recipientEmail - Optional recipient email/name to display in callout
 * @param variable - Optional SpaceVariable object for icon determination
 * @returns Object containing the variableWrapperNode and caretAnchorNode
 */
function createVariableNodes(
  schema: any,
  variableText: string,
  hasDefault: boolean,
  hasRecipient: boolean,
  variableId?: string,
  variableName?: string,
  recipientEmail?: string,
  variable?: SpaceVariable,
  allowAssignCallout: boolean = true
) {
  const iconState = variable ? getVariableIconState(variable) : null

  // Build mark attributes
  const markAttributes: Record<string, string | null> = {
    'data-has-default': hasDefault ? 'true' : 'false'
  }

  if (hasRecipient) {
    markAttributes['data-has-recipient'] = 'true'
  }
  if (variable) {
    const assignedToCurrentUserAttr = getAssignedToCurrentUserAttr(variable)
    if (assignedToCurrentUserAttr) {
      markAttributes['data-assigned-to-current-user'] = assignedToCurrentUserAttr
    }
  }

  if (variableId) {
    markAttributes['data-variable-id'] = variableId
  }
  if (variableName) {
    markAttributes['data-variable-name'] = variableName
  }
  if (iconState) {
    markAttributes['data-icon-state'] = iconState
  }
  if (iconState === 'pend') {
    if (recipientEmail) {
      markAttributes['data-avatar-letter'] = getAvatarInitial(undefined, recipientEmail)
      markAttributes['data-avatar-color-index'] = getAvatarColorIndex(recipientEmail).toString()
    }
  }

  // Create nodes
  const variablePlaceholderMark = schema.marks.variablePlaceholder.create(markAttributes)
  const variableTextNode = schema.text(variableText, [variablePlaceholderMark])
  const wrapperContent = [variableTextNode]

  // Add assign-callout for all variables (shown on hover)
  if (allowAssignCallout) {
    wrapperContent.push(schema.nodes.assignCallout.create(buildAssignCalloutAttributes(variable, recipientEmail)))
  }

  return {
    variableWrapperNode: schema.nodes.variableWrapper.create({}, wrapperContent),
    caretAnchorNode: schema.nodes.caretAnchor.create()
  }
}

/**
 * Checks if a variable text content is empty or only contains whitespace
 * @param textContent - The text content from the variable span
 * @returns true if variable is empty or only whitespace, false otherwise
 */
function isVariableEmpty(textContent: string): boolean {
  if (!textContent) {
    return true
  }

  const cleanedText = stripLegacyIconPrefix(textContent).trim()

  // First check if it's just empty braces (no content at all)
  // This handles cases like "{{ }}" or "{{  }}" where pattern might not match
  if (/^\{\{\s*\}\}$/.test(cleanedText)) {
    return true
  }

  // Extract content between braces using VARIABLE_TEXT_PATTERN
  const match = cleanedText.match(VARIABLE_TEXT_PATTERN)
  if (!match) {
    // If pattern doesn't match, check if it looks like empty braces
    // This handles edge cases where text might be malformed
    if (cleanedText.includes('{{') && cleanedText.includes('}}')) {
      // Extract everything between {{ and }}
      const braceMatch = cleanedText.match(/\{\{(.*?)\}\}/)
      if (braceMatch) {
        const content = braceMatch[1] || ''
        return !content.trim() || /^\s*$/.test(content)
      }
    }
    return true
  }

  // Get the content between braces (match[1] is the captured group)
  const contentBetweenBraces = match[1] || ''

  // Check if content is empty or only whitespace
  const trimmedContent = contentBetweenBraces.trim()
  return !trimmedContent || trimmedContent === ''
}

/**
 * Deletes the entire variable element including caret anchor
 * @param view - ProseMirror view
 * @param targetSpan - The target span element (variable-wrapper or variable-placeholder)
 * @param spanStart - The start position of the span in the document
 * @param docSize - The size of the document
 * @returns true if deletion was successful, false otherwise
 */
function deleteEntireVariable(view: any, targetSpan: HTMLElement, spanStart: number, docSize: number): boolean {
  if (spanStart < 0 || spanStart >= docSize) {
    return false
  }

  const wrapperElement = targetSpan.classList.contains('variable-wrapper')
    ? targetSpan
    : (targetSpan.closest('.variable-wrapper') as HTMLElement | null)

  if (wrapperElement) {
    const wrapperText = wrapperElement.textContent || ''
    const hasVariableText = wrapperText.includes('{{') && wrapperText.includes('}}')
    const hasOnlyCallout =
      !hasVariableText && wrapperElement.querySelector('.assign-callout') && wrapperElement.children.length === 1
    if (hasOnlyCallout) {
      const wrapperStart = view.posAtDOM(wrapperElement, 0)
      if (wrapperStart !== null && wrapperStart !== -1) {
        const wrapperNode = view.state.doc.nodeAt(wrapperStart)
        if (wrapperNode) {
          let wrapperEnd = wrapperStart + wrapperNode.nodeSize
          const nodeAfter = view.state.doc.nodeAt(wrapperEnd)
          if (nodeAfter && nodeAfter.type.name === 'caretAnchor') {
            wrapperEnd += nodeAfter.nodeSize
          }
          if (wrapperStart >= 0 && wrapperEnd <= docSize && wrapperStart < wrapperEnd) {
            const tr = view.state.tr.deleteRange(wrapperStart, wrapperEnd)
            tr.setSelection(TextSelection.create(tr.doc, wrapperStart))
            view.dispatch(tr)
            return true
          }
        }
      }
    }
  }

  // Fast path: delete the variable-wrapper node directly when we have it
  if (targetSpan.classList.contains('variable-wrapper')) {
    const nodeStart = view.posAtDOM(targetSpan, 0)
    if (nodeStart !== null && nodeStart !== -1) {
      const node = view.state.doc.nodeAt(nodeStart)
      if (node) {
        let nodeEnd = nodeStart + node.nodeSize
        const nodeAfter = view.state.doc.nodeAt(nodeEnd)
        if (nodeAfter && nodeAfter.type.name === 'caretAnchor') {
          nodeEnd += nodeAfter.nodeSize
        }
        if (nodeStart >= 0 && nodeEnd <= docSize && nodeStart < nodeEnd) {
          const tr = view.state.tr.deleteRange(nodeStart, nodeEnd)
          tr.setSelection(TextSelection.create(tr.doc, nodeStart))
          view.dispatch(tr)
          return true
        }
      }
    }
  }

  // Prefer deleting the entire variable-wrapper node when possible
  try {
    const resolvedPos = view.state.doc.resolve(spanStart)
    for (let depth = resolvedPos.depth; depth > 0; depth--) {
      const nodeAtDepth = resolvedPos.node(depth)
      if (nodeAtDepth.type.name === 'variableWrapper') {
        const from = resolvedPos.before(depth)
        let to = resolvedPos.after(depth)
        const nodeAfter = view.state.doc.nodeAt(to)
        if (nodeAfter && nodeAfter.type.name === 'caretAnchor') {
          to += nodeAfter.nodeSize
        }
        if (from >= 0 && to <= docSize && from < to) {
          const tr = view.state.tr.deleteRange(from, to)
          tr.setSelection(TextSelection.create(tr.doc, from))
          view.dispatch(tr)
          return true
        }
        break
      }
    }
  } catch (error) {
    // Fall back to legacy deletion logic below
  }

  // Calculate end position including caret anchor
  const textContent = targetSpan.textContent || ''
  let spanEnd = spanStart + textContent.length

  // Check for caret-anchor span after variable
  const nextSibling = targetSpan.nextSibling

  if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE) {
    const nextText = nextSibling.textContent || ''
    if (nextText.startsWith('\u200B')) {
      spanEnd += 1
    }
  } else if (nextSibling && (nextSibling as HTMLElement).classList?.contains('caret-anchor')) {
    // Check for caret-anchor span element
    const caretAnchorStart = view.posAtDOM(nextSibling, 0)
    if (caretAnchorStart !== null && caretAnchorStart !== -1) {
      spanEnd = caretAnchorStart + 1
    }
  }

  // Validate positions before deleting
  if (spanStart >= 0 && spanEnd <= docSize && spanStart < spanEnd) {
    // Prefer deleting exact node range when possible to avoid removing adjacent content
    if (targetSpan.nodeType === Node.ELEMENT_NODE) {
      const nodeStart = view.posAtDOM(targetSpan, 0)

      if (nodeStart !== null && nodeStart !== -1) {
        const resolvedPos = view.state.doc.resolve(nodeStart)
        const parentNode = resolvedPos.parent
        const parentStart = resolvedPos.start(resolvedPos.depth)
        const parentEnd = parentStart + parentNode.nodeSize

        const node = view.state.doc.nodeAt(nodeStart)

        if (node) {
          let nodeEnd = nodeStart + node.nodeSize
          const nodeAfter = view.state.doc.nodeAt(nodeEnd)

          if (nodeAfter && nodeAfter.type.name === 'caretAnchor') {
            // Only include caret anchor if it's within the same parent paragraph
            const caretAnchorEnd = nodeEnd + nodeAfter.nodeSize
            if (caretAnchorEnd <= parentEnd) {
              nodeEnd = caretAnchorEnd
            } else {
              // Caret anchor extends beyond parent - clamp to parent end
              nodeEnd = parentEnd
            }
          }

          // Ensure nodeEnd never exceeds parent boundary
          if (nodeEnd > parentEnd) {
            nodeEnd = parentEnd
          }

          if (nodeStart >= 0 && nodeEnd <= docSize && nodeStart < nodeEnd && nodeEnd <= parentEnd) {
            const tr = view.state.tr.deleteRange(nodeStart, nodeEnd)
            tr.setSelection(TextSelection.create(tr.doc, nodeStart))
            view.dispatch(tr)
            return true
          }
        }
      }
    }

    // Ensure spanEnd doesn't exceed parent paragraph boundary
    const resolvedPos = view.state.doc.resolve(spanStart)
    const parentNode = resolvedPos.parent
    const parentStart = resolvedPos.start(resolvedPos.depth)
    const parentEnd = parentStart + parentNode.nodeSize

    if (spanEnd > parentEnd) {
      spanEnd = parentEnd
    }

    if (spanStart >= 0 && spanEnd <= docSize && spanStart < spanEnd && spanEnd <= parentEnd) {
      const tr = view.state.tr.deleteRange(spanStart, spanEnd)
      tr.setSelection(TextSelection.create(tr.doc, spanStart))
      view.dispatch(tr)
      return true
    }
  }

  return false
}

function validateAndFinalizeVariable(innerSpan: HTMLElement, view: any, editor: any, allowAssignCallout: boolean) {
  // Find the variable-placeholder span (which may be inside variable-wrapper)
  const placeholderSpan = innerSpan.closest('.variable-placeholder') as HTMLElement
  if (!placeholderSpan || placeholderSpan.getAttribute('data-variable-editable') !== 'true') {
    return
  }

  // Find the variable-wrapper if it exists, otherwise use placeholderSpan
  const variableWrapper = placeholderSpan.closest('.variable-wrapper') as HTMLElement
  const targetSpan = variableWrapper || placeholderSpan

  // Extract variable name from inner span text (format: {{ variableName }})
  const text = innerSpan.textContent || ''
  const cleanedText = stripLegacyIconPrefix(text).trim()
  const match = cleanedText.match(VARIABLE_TEXT_PATTERN)
  if (!match) {
    return
  }

  const variableName = match[1].trim()

  // Validate variable name
  if (!variableName || !VARIABLE_NAME_REGEX.test(variableName)) {
    // Invalid variable name - remove the variable
    const pos = view.posAtDOM(targetSpan, 0)
    if (pos === null || pos === -1) return

    // Calculate end position including caret anchor
    const textContent = targetSpan.textContent || ''
    let endPos = pos + textContent.length

    // Check for caret-anchor span after variable
    const nextSibling = targetSpan.nextSibling
    if (nextSibling && (nextSibling as HTMLElement).classList?.contains('caret-anchor')) {
      const caretAnchorStart = view.posAtDOM(nextSibling, 0)
      if (caretAnchorStart !== null && caretAnchorStart !== -1) {
        endPos = caretAnchorStart + 1
      }
    } else {
      endPos += 1 // +1 for zero-width space if no separate caret-anchor
    }

    view.dispatch(view.state.tr.deleteRange(pos, endPos))
    return
  }

  // Valid variable - convert to non-editable with new nested structure
  const variableText = `{{ ${variableName} }}`
  const pos = view.posAtDOM(targetSpan, 0)
  if (pos === null || pos === -1) return

  // Look up variable in store to get ID and check for default/assignment
  let variableId: string | undefined
  let hasDefault = false
  let hasRecipient = false
  let recipientEmail: string | undefined

  const variable = findVariableByName(variableName)
  if (variable) {
    variableId = variable.id
    const flags = getVariableValueFlags(variable)
    hasRecipient = flags.hasRecipient
    hasDefault = flags.hasDefault
    // Extract recipient email/name if available
    recipientEmail = variable.assignedToRecipientName?.trim() || undefined
  } else {
    // Variable not found in store, use default state
    const state = getVariableState(variableName)
    hasDefault = state.hasDefault
    hasRecipient = state.hasRecipient
    recipientEmail = state.recipientEmail
  }

  // Replace the editable variable with non-editable version using new nested structure
  // Use ProseMirror transaction API to insert nodes properly
  const state = editor.state
  const { schema } = state

  // Create variable nodes with proper attributes including variable ID, name, recipient email, and variable object for icon
  const { variableWrapperNode, caretAnchorNode } = createVariableNodes(
    schema,
    variableText,
    hasDefault,
    hasRecipient,
    variableId,
    variableName,
    recipientEmail,
    variable,
    allowAssignCallout
  )

  // Create transaction to replace selection with new nodes
  const tr = state.tr
  tr.replaceSelectionWith(variableWrapperNode, false)
  const newPos = tr.selection.from
  tr.insert(newPos, caretAnchorNode)

  // Apply transaction
  view.dispatch(tr)

  // Set cursor position after variable
  setTimeout(() => {
    editor
      .chain()
      .focus()
      .setTextSelection(newPos + variableText.length + 1)
      .run()
  }, 0)
}

/**
 * Finds all placeholder instances in the editor by variable ID or name
 * @param editor - The TipTap editor instance
 * @param variableId - Optional variable ID to match
 * @param variableName - Optional variable name to match (used as fallback)
 * @returns Array of positions and ranges for each placeholder instance
 */
export function findAllPlaceholderInstances(
  editor: any,
  variableId?: string,
  variableName?: string
): Array<{ from: number; to: number; wrapperFrom?: number; wrapperTo?: number }> {
  const { state } = editor
  const { doc } = state
  const variablePlaceholderMarkType = state.schema.marks.variablePlaceholder
  const variableWrapperNodeType = state.schema.nodes.variableWrapper

  if (!variablePlaceholderMarkType) {
    return []
  }

  const instances: Array<{ from: number; to: number; wrapperFrom?: number; wrapperTo?: number }> = []

  // Traverse document to find all placeholder instances
  doc.descendants((node: any, pos: number) => {
    // Check for variable wrapper nodes first
    if (variableWrapperNodeType && node.type === variableWrapperNodeType) {
      // Find text node with variablePlaceholder mark inside wrapper
      node.descendants((childNode: any, childPos: number) => {
        if (childNode.isText && childNode.marks) {
          const mark = childNode.marks.find((m: any) => m.type === variablePlaceholderMarkType)
          if (mark) {
            const markVariableId = mark.attrs['data-variable-id']
            const markVariableName = mark.attrs['data-variable-name']

            // Match by variable ID (primary) OR variable name (fallback)
            let isMatch = false
            if (variableId && markVariableId === variableId) {
              isMatch = true
            } else if (
              variableName &&
              markVariableName &&
              markVariableName.toLowerCase() === variableName.toLowerCase()
            ) {
              isMatch = true
            }
            if (isMatch) {
              const textFrom = pos + childPos
              const textTo = textFrom + childNode.nodeSize

              // Check for caret anchor after wrapper
              let wrapperEnd = pos + node.nodeSize
              const nextSibling = doc.nodeAt(wrapperEnd)
              if (nextSibling && nextSibling.type.name === 'caretAnchor') {
                wrapperEnd += nextSibling.nodeSize
              }

              instances.push({
                from: textFrom,
                to: textTo,
                wrapperFrom: pos,
                wrapperTo: wrapperEnd
              })
            }
          }
        }
      })
      return false // Don't traverse children again
    }

    // Also check for standalone text nodes with variablePlaceholder mark (backward compatibility)
    if (node.isText && node.marks) {
      const mark = node.marks.find((m: any) => m.type === variablePlaceholderMarkType)
      if (mark) {
        const markVariableId = mark.attrs['data-variable-id']
        const markVariableName = mark.attrs['data-variable-name']

        // Match by variable ID (primary) OR variable name (fallback)
        let isMatch = false
        if (variableId && markVariableId === variableId) {
          isMatch = true
        } else if (variableName && markVariableName && markVariableName.toLowerCase() === variableName.toLowerCase()) {
          isMatch = true
        }

        if (isMatch) {
          // Find parent wrapper if exists
          let wrapperFrom: number | undefined
          let wrapperTo: number | undefined

          // Check if there's a wrapper parent
          const parentPos = pos - node.nodeSize

          // Try to find wrapper by checking previous nodes
          if (parentPos >= 0) {
            const parentNode = doc.nodeAt(parentPos)
            if (parentNode && parentNode.type.name === 'variableWrapper') {
              wrapperFrom = parentPos
              wrapperTo = parentPos + parentNode.nodeSize

              // Check for caret anchor after wrapper
              const nextSibling = doc.nodeAt(wrapperTo)
              if (nextSibling && nextSibling.type.name === 'caretAnchor') {
                wrapperTo += nextSibling.nodeSize
              }
            }
          }

          const textFrom = pos
          const textTo = pos + node.nodeSize

          // If wrapper found, use wrapper range, otherwise use text range
          instances.push({
            from: wrapperFrom !== undefined ? wrapperFrom : textFrom,
            to: wrapperTo !== undefined ? wrapperTo : textTo,
            wrapperFrom: wrapperFrom,
            wrapperTo: wrapperTo
          })
        }
      }
    }

    return true
  })

  return instances
}

/**
 * Deletes all placeholder instances of a variable from the editor
 * @param editor - The TipTap editor instance
 * @param variableId - Optional variable ID to match
 * @param variableName - Optional variable name to match (used as fallback)
 * @returns true if any instances were deleted, false otherwise
 */
export function deleteAllPlaceholderInstances(
  editor: any,
  variableId?: string,
  variableName?: string,
  options?: { focusAndSelect?: boolean }
): boolean {
  const focusAndSelect = options?.focusAndSelect !== false
  const instances = findAllPlaceholderInstances(editor, variableId, variableName)

  if (instances.length === 0) {
    return false
  }

  // Sort instances by position in reverse order to maintain correct positions during deletion
  instances.sort((a, b) => b.from - a.from)

  const { state } = editor
  let tr = state.tr

  // Replace each instance in reverse order with typing format placeholder
  // We need to collect positions first, then replace after deletion
  const replacementPositions: Array<{ pos: number }> = []

  for (const instance of instances) {
    // Prefer wrapper range if available (replaces entire wrapper including wrapper node)
    // Otherwise fall back to text range (for backward compatibility with standalone text nodes)
    const from = instance.wrapperFrom !== undefined ? instance.wrapperFrom : instance.from
    const to = instance.wrapperTo !== undefined ? instance.wrapperTo : instance.to

    // Validate positions
    if (from >= 0 && to <= state.doc.content.size && from < to) {
      // Delete the old variable
      tr = tr.deleteRange(from, to)
      // Store position for replacement (after deletion, position will be 'from')
      replacementPositions.push({ pos: from })
    }
  }

  // Only dispatch if there are changes
  if (tr.steps.length > 0) {
    const emptyPlaceholder = '{{  }}'
    const variablePlaceholderMarkType = state.schema.marks.variablePlaceholder
    let lastSelectionPos: number | null = null

    // Insert placeholders in descending order to keep positions stable
    for (const { pos } of replacementPositions) {
      const mappedPos = tr.mapping.map(pos, -1)
      tr = tr.insert(mappedPos, state.schema.text(emptyPlaceholder))
      if (variablePlaceholderMarkType) {
        tr = tr.addMark(mappedPos, mappedPos + emptyPlaceholder.length, variablePlaceholderMarkType.create())
      }
      lastSelectionPos = mappedPos + 3
    }

    if (focusAndSelect && lastSelectionPos !== null) {
      const selectionPos = Math.min(lastSelectionPos, tr.doc.content.size)
      tr = tr.setSelection(TextSelection.create(tr.doc, selectionPos))
    }

    editor.view.dispatch(tr)
    if (focusAndSelect) {
      editor.commands.focus()
    }
    return true
  }

  return false
}

/**
 * Deletes all placeholder instances of a variable from ALL editor instances across the application
 * Traverses the Vue component tree to find all MessageElementComponent instances and deletes
 * placeholders from each editor.
 *
 * @param vueRoot - Vue instance to start traversal from (typically the root component)
 * @param variableId - Optional variable ID to match
 * @param variableName - Optional variable name to match (used as fallback)
 * @returns Number of editors that had placeholders deleted
 */
export function deleteAllPlaceholderInstancesFromAllEditors(
  vueRoot: any,
  variableId?: string,
  variableName?: string,
  activeEditor?: any
): number {
  if (!vueRoot) {
    return 0
  }

  // Find root Vue instance
  let root = vueRoot
  while (root.$parent) {
    root = root.$parent
  }

  const allComponents: any[] = []
  // Static-rendered blocks (table cells) that hold stored content but no editor yet
  const staticComponents: any[] = []

  // Traverse Vue component tree to find all MessageElementComponent instances
  // MessageElementComponent has an 'editor' property that contains the Tiptap Editor instance
  const findComponents = (component: any): void => {
    // The off-tree pool host holds the live editor; do not also walk its TextEditor child
    if (component?.isPooledEditorHost && component.editor && typeof component.editor.getHTML === 'function') {
      allComponents.push(component)
      return
    }
    // Check if component has an editor property (MessageElementComponent pattern)
    // Also check component name to be more specific
    if (component && component.editor && typeof component.editor.getHTML === 'function') {
      // This looks like a component with a Tiptap editor
      allComponents.push(component)
    } else if (component && typeof component.removeVariablePlaceholders === 'function') {
      staticComponents.push(component)
    }

    // Recursively check children
    if (component.$children && component.$children.length > 0) {
      component.$children.forEach((child: any) => findComponents(child))
    }
  }

  // Start traversal from root
  findComponents(root)

  // Delete placeholders from each editor and update underlying message
  let editorsUpdated = 0
  for (const component of allComponents) {
    if (component.editor) {
      const deleted = deleteAllPlaceholderInstances(component.editor, variableId, variableName, {
        focusAndSelect: component.editor === activeEditor
      })
      if (deleted) {
        editorsUpdated++

        // Wait for the placeholder insertion to complete, then save the message
        // Use a longer delay to ensure insertion is done
        setTimeout(async () => {
          // Trigger save to persist the change to backend
          // This ensures the deleted variable replacement is saved and won't reappear on reload
          if (component.messageElement && component.messageElement.message && component.editor) {
            if (component.updateMessage && typeof component.updateMessage === 'function') {
              try {
                // updateMessage() reads editor.getHTML() and, when it differs from the stored message, queues
                // the save; the save coordinator writes it into the store at once. Writing the store here as well
                // would mark the change saved before (or even if not) the server has it.
                await component.updateMessage()
              } catch (error: any) {
                console.error('Failed to persist variable placeholder update', error)
              }
            }
          }
        }, 100) // Wait 100ms for insertion to complete
      }
    }
  }

  // Their stored HTML must lose the variable too, not only their display
  for (const component of staticComponents) {
    if (component.removeVariablePlaceholders(variableId, variableName)) {
      editorsUpdated++
    }
  }

  return editorsUpdated
}
